import React, { useState } from 'react';
import OnboardingLayout from '../layouts/OnboardingLayout';
import Button from 'components/v2/button/Button';
import { Checkbox } from 'components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import CountryCodeDropdown from 'modules/auth/components/CountryCodeDropDown';
import countryCodes from 'data/country-codes.json';
import {
  useSendOtpMutation,
  useWhitelistUsersMutation,
} from 'modules/auth/services/verification.mutation.service';
import { Loader } from 'lucide-react';
import { useAuth } from 'modules/auth/context/AuthContextV2';

type Props = {
  onStartClick: () => void;
  onBackClick: () => void;
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
  onboardingProgress: number;
};

const PhoneNumber: React.FC<Props> = ({
  onStartClick,
  onBackClick,
  phoneNumber,
  onPhoneNumberChange,
  onboardingProgress,
}) => {
  const navigate = useNavigate();
  const [isChecked, setIsChecked] = useState(false);
  const [sendOtp, { isLoading }] = useSendOtpMutation();
  const [error, setError] = useState<string | null>(null);
  const [whitelistUsers] = useWhitelistUsersMutation();
  const { login } = useAuth();
  const handleSendOtp = async () => {
    try {
      setError(null);
      // Format phone number to remove any non-digit characters
      const formattedPhoneNumber = `+1${phoneNumber.replace(/\D/g, '')}`;

      // Send OTP to the phone number

      const bypassNumbers = process.env.REACT_APP_BYPASS_NUMBERS
        ? JSON.parse(process.env.REACT_APP_BYPASS_NUMBERS)
        : [];

      const isDevOrStaging =
        process.env.REACT_APP_CHANNEL === 'develop' ||
        process.env.REACT_APP_CHANNEL === 'staging' ||
        process.env.REACT_APP_CHANNEL === 'local' ||
        process.env.REACT_APP_CHANNEL === 'production';
      const isBypassNumber = bypassNumbers.includes(formattedPhoneNumber);

      // If in dev or staging, and the phone number is in the bypass list, skip verification
      if (isDevOrStaging && isBypassNumber) {
        console.log('Bypass number detected, skipping verification');
        // Move to the next step automatically
        const response = await whitelistUsers({
          phoneNumber: formattedPhoneNumber,
        }).unwrap();

        login(response);
        onStartClick();
        return; // Early return to skip sendOtp for bypass numbers
      }

      await sendOtp({
        phoneNumber: formattedPhoneNumber,
      }).unwrap();

      // Move to the next step if successful
      onStartClick();
    } catch (err) {
      console.error('Failed to send OTP:', err);
      setError('Failed to send verification code. Please try again.');
    }
  };

  return (
    <OnboardingLayout
      onBackClick={onBackClick}
      actionChildren={
        <div className="flex flex-col justify-center items-center gap-5">
          <div className="flex items-center gap-2 text-[0.875rem] font-bold">
            <div className="flex items-center justify-center">
              <Checkbox
                checked={isChecked}
                onCheckedChange={(checked) => setIsChecked(checked === true)}
              />
            </div>
            <div>
              I agree to the{' '}
              <span className="underline" onClick={() => navigate('/terms')}>
                terms and conditions
              </span>
            </div>
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <Button
            disabled={!isChecked || phoneNumber.length < 10 || isLoading}
            onClick={handleSendOtp}
            className="flex items-center justify-center"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader className="animate-spin h-4 w-4" />
                <span>Sending...</span>
              </div>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      }
      onboardingProgress={onboardingProgress}
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-8">
          <div className="text-[2rem] font-bold">Create your account</div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-3 text-[0.875rem]">
              <div className="flex justify-between items-center gap-2">
                <label className="text-[0.875rem]">
                  What's your phone number
                </label>
                <div>*</div>
              </div>
              <CountryCodeDropdown
                className="w-full p-2 rounded-xl"
                countryCodes={countryCodes}
                selectedCode="US"
                phoneNumber={phoneNumber}
                onCodeSelect={() => {}}
                onPhoneNumberChange={onPhoneNumberChange}
                placeHolder={'(000) 000-0000'}
                inputClassName=""
                triggerClassName=""
              />
            </div>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default PhoneNumber;
